import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from 'context/StoreContext';
import useCategories from 'hooks/useCategories';
import { whatsappDigits, whatsappUrl } from 'utils/whatsapp';
import Container from './Container';
import usePaymentCopy from 'utils/paymentCopy';
import { QrIcon, WalletIcon } from './icons';

const linkClass =
  'inline-flex min-h-[40px] items-center text-primary-contrast/80 underline-offset-4 transition-colors hover:text-primary-contrast hover:underline';

function Column({ id, title, children }) {
  return (
    <nav aria-labelledby={id}>
      <h2 id={id} className="text-[12px] font-semibold uppercase tracking-[0.14em] text-primary-contrast/60">
        {title}
      </h2>
      <ul className="mt-3">{children}</ul>
    </nav>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  const pay = usePaymentCopy();
  const { identity, isPlaceholder, infoPages, showArticles } = useStore();
  const categories = useCategories();
  const wa = whatsappUrl(identity.whatsapp);
  const page = (slug) => infoPages.find((p) => p.slug === slug);
  const helpSlugs = ['faq', 'pengiriman-pengembalian', 'kontak', 'tentang-kami'].filter(page);
  const pageLink = (slug) => {
    const p = page(slug);
    return (
      p && (
        <li key={slug}>
          <Link className={linkClass} to={`/halaman/${slug}`}>
            {t(p.titleKey)}
          </Link>
        </li>
      )
    );
  };
  const place = [identity.address.city, identity.address.province].filter(Boolean).join(', ');

  return (
    <footer className="on-dark mt-auto bg-primary text-primary-contrast">
      <Container className="grid gap-10 pb-10 pt-14 md:grid-cols-12 md:gap-8 lg:pt-20">
        <div className="md:col-span-4 lg:col-span-5">
          {!isPlaceholder && (
            <>
              <p className="font-display text-4xl leading-none">{identity.name}</p>
              {identity.tagline && (
                <p className="mt-4 max-w-xs text-pretty text-primary-contrast/75">{identity.tagline}</p>
              )}
              {place && <p className="mt-4 text-sm text-primary-contrast/60">{place}</p>}
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 md:col-span-8 lg:col-span-7">
          <Column id="footer-shop" title={t('footer.shop')}>
            <li>
              <Link className={linkClass} to="/kategori/semua">
                {t('header.allProducts')}
              </Link>
            </li>
            {categories.map((c) => (
              <li key={c.slug}>
                <Link className={linkClass} to={`/kategori/${c.slug}`}>
                  {c.name}
                </Link>
              </li>
            ))}
            {showArticles && (
              <li>
                <Link className={linkClass} to="/artikel">
                  {t('articles.nav')}
                </Link>
              </li>
            )}
          </Column>
          <Column id="footer-help" title={t('footer.help')}>
            <li>
              <Link className={linkClass} to="/pesanan-saya">
                {t('header.myOrders')}
              </Link>
            </li>
            {helpSlugs.map(pageLink)}
          </Column>
          <div className="col-span-2 sm:col-span-1">
            <Column id="footer-contact" title={t('footer.contact')}>
              {wa && (
                <li>
                  <a className={linkClass} href={wa} target="_blank" rel="noopener noreferrer">
                    WhatsApp {whatsappDigits(identity.whatsapp).replace(/^62/, '0')}
                  </a>
                </li>
              )}
              {identity.email && (
                <li>
                  <a className={`${linkClass} break-all`} href={`mailto:${identity.email}`}>
                    {identity.email}
                  </a>
                </li>
              )}
              {!wa && !identity.email && <li className="text-primary-contrast/70">{t('footer.noContact')}</li>}
            </Column>
          </div>
        </div>
      </Container>
      <Container>
        <div className="flex flex-col gap-4 border-t border-primary-contrast/15 py-6 text-[13px] text-primary-contrast/65 sm:flex-row sm:items-center sm:justify-between">
        <p>{t('footer.copyright', { year: new Date().getFullYear(), name: identity.name })}</p>
        <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
          {['kebijakan-privasi', 'syarat-ketentuan'].filter(page).map((slug) => (
            <li key={slug}>
              <Link className="underline-offset-4 hover:text-primary-contrast hover:underline" to={`/halaman/${slug}`}>
                {t(page(slug).titleKey)}
              </Link>
            </li>
          ))}
          <li className="flex items-center gap-1.5">
            {pay.qrisOnly ? <QrIcon className="h-4 w-4" /> : <WalletIcon className="h-4 w-4" />}
            {pay.summary ? t('footer.payWith', { methods: pay.summary }) : t('footer.payOnline')}
          </li>
          </ul>
        </div>
      </Container>
    </footer>
  );
}
